import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, firstValueFrom, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthUtilsService } from './auth-utils.service';
import { environment } from '../../environments/environment';

export interface ApiErrorResponse {
  status: number;
  message: string;
  error?: any;
}

export interface LoadOptions {
  useAuth?: boolean;
  includeContentType?: boolean;
  baseUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
    private readonly http: HttpClient,
    private readonly authUtils: AuthUtilsService
  ) {}

  /**
   * Makes an authenticated GET request
   */
  async get<T>(endpoint: string, options: LoadOptions = {}): Promise<T> {
    const url = this.buildUrl(endpoint, options.baseUrl);
    const headers = (options.useAuth === false) ? {} : this.authUtils.createAuthHeaders(options.includeContentType);
    
    try {
      return await firstValueFrom(
        this.http.get<T>(url, { headers }).pipe(
          catchError(this.handleError)
        )
      );
    } catch (error) {
      throw this.transformError(error);
    }
  }

  /**
   * Makes an authenticated POST request
   */
  async post<T>(endpoint: string, body: any, options: LoadOptions = {}): Promise<T> {
    const url = this.buildUrl(endpoint, options.baseUrl);
    const headers = (options.useAuth === false) ? {} : this.authUtils.createAuthHeaders(options.includeContentType);
    
    try {
      return await firstValueFrom(
        this.http.post<T>(url, body, { headers }).pipe(
          catchError(this.handleError)
        )
      );
    } catch (error) {
      throw this.transformError(error);
    }
  }

  /**
   * Makes an authenticated PUT request
   */
  async put<T>(endpoint: string, body: any, options: LoadOptions = {}): Promise<T> {
    const url = this.buildUrl(endpoint, options.baseUrl);
    const headers = (options.useAuth === false) ? {} : this.authUtils.createAuthHeaders(options.includeContentType);
    
    try {
      return await firstValueFrom(
        this.http.put<T>(url, body, { headers }).pipe(
          catchError(this.handleError)
        )
      );
    } catch (error) {
      throw this.transformError(error);
    }
  }

  /**
   * Makes an authenticated DELETE request
   */
  async delete<T>(endpoint: string, options: LoadOptions = {}): Promise<T> {
    const url = this.buildUrl(endpoint, options.baseUrl);
    const headers = (options.useAuth === false) ? {} : this.authUtils.createAuthHeaders(options.includeContentType);
    
    try {
      return await firstValueFrom(
        this.http.delete<T>(url, { headers }).pipe(
          catchError(this.handleError)
        )
      );
    } catch (error) {
      throw this.transformError(error);
    }
  }

  /**
   * Builds full URL from endpoint
   */
  private buildUrl(endpoint: string, baseUrl?: string): string {
    const base = baseUrl || environment.apiBaseUrl;
    return endpoint.startsWith('http') ? endpoint : `${base}${endpoint}`;
  }

  /**
   * Handles HTTP errors in a consistent way
   */
  private readonly handleError = (error: HttpErrorResponse): Observable<never> => {
    console.warn('API Error:', error);
    return throwError(() => error);
  };

  /**
   * Transforms HTTP errors into a consistent format
   */
  private transformError(error: any): ApiErrorResponse {
    if (error instanceof HttpErrorResponse) {
      return {
        status: error.status,
        message: this.getErrorMessage(error.status),
        error: error.error
      };
    }
    
    return {
      status: 0,
      message: 'Unknown error occurred',
      error
    };
  }

  /**
   * Gets user-friendly error messages based on HTTP status codes
   */
  private getErrorMessage(status: number): string {
    const errorMessages: Record<number, string> = {
      400: 'Bad request - please check your input',
      401: 'Authentication failed - please login again',
      403: 'Permission denied',
      404: 'Resource not found',
      409: 'Conflict - resource already exists or has been modified',
      422: 'Validation failed - please check your input',
      429: 'Too many requests - please try again later',
      500: 'Server error - please try again later',
      502: 'Bad gateway - service temporarily unavailable',
      503: 'Service unavailable - please try again later',
      504: 'Gateway timeout - request took too long',
      // Cloudflare-specific status codes
      520: 'Unknown server error - please try again',
      521: 'Server is down - please try again later',
      522: 'Connection timed out - please try again',
      523: 'Origin unreachable - please try again later',
      524: 'Request timed out - please try again'
    };
    
    return errorMessages[status] || `HTTP Error ${status}`;
  }

  /**
   * Checks if the user is online
   */
  isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Checks if an error indicates authentication failure
   */
  isAuthError(error: ApiErrorResponse): boolean {
    return error.status === 401 || error.status === 403;
  }
}